import React, { useState, useEffect } from 'react';
import { SportsCard } from '../types';
import { X, Copy, Check, ExternalLink, ShoppingBag, Globe, ShoppingCart, Search, ClipboardList, Link2, Loader2, Share2, Layers } from 'lucide-react';
import { authService } from '../services/authService';

interface ListingModalProps {
  card: SportsCard;
  onClose: () => void;
}

// Marketplace configurations
const getMarketplaces = (c: SportsCard) => [
  {
    id: 'ebay',
    name: 'eBay',
    icon: ShoppingBag,
    color: 'text-red-600',
    bgColor: 'bg-red-50',
    searchUrl: `https://www.ebay.com/sch/i.html?_nkw=${encodeURIComponent(`${c.year} ${c.manufacturer} ${c.player} ${c.cardNumber}`)}`,
    createUrl: `https://www.ebay.com/sl/prelist/suggest?q=${encodeURIComponent(`${c.year} ${c.manufacturer} ${c.player} ${c.cardNumber}`)}`,
    apiCapable: true,
  },
  {
    id: 'myslabs',
    name: 'MySlabs',
    icon: Layers,
    color: 'text-blue-800',
    bgColor: 'bg-blue-50',
    searchUrl: `https://myslabs.com/search/slabs/?q=${encodeURIComponent(c.player)}`,
    createUrl: `https://myslabs.com/add/slab/`,
    apiCapable: false,
  },
  {
    id: 'fb',
    name: 'Facebook',
    icon: Globe,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    searchUrl: `https://www.facebook.com/marketplace/search/?query=${encodeURIComponent(`${c.player} ${c.year} Card`)}`,
    createUrl: `https://www.facebook.com/marketplace/create/item`,
    apiCapable: false,
  },
];

const ListingModal: React.FC<ListingModalProps> = ({ card, onClose }) => {

  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [selectedMarkets, setSelectedMarkets] = useState<string[]>(['ebay', 'myslabs']);
  const [connectedAccounts, setConnectedAccounts] = useState<Record<string, boolean>>({
    ebay: authService.isAuthenticated('ebay'),
  });
  const [isPosting, setIsPosting] = useState<Record<string, boolean>>({});
  const [googleUser, setGoogleUser] = useState<any>(null);
  const [statusToast, setStatusToast] = useState<string | null>(null);

  const marketplaces = getMarketplaces(card);

  const copyToClipboard = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const toggleMarket = (id: string) => {
    setSelectedMarkets(prev =>
      prev.includes(id) ? prev.filter(m => m !== id) : [...prev, id]
    );
  };

  const copyFullListing = () => {
    const fullText = `${card.listingDraft.title}\n\nPrice: $${card.estimatedValue.min}\nCondition: ${card.isGraded ? `${card.grader} ${card.gradeScore}` : 'Raw'}\n\nCorners: ${card.conditionDetails.corners}\nSurface: ${card.conditionDetails.surface}\n\n${card.listingDraft.description}\n\n${card.listingDraft.tags.map(t => `#${t}`).join(' ')}`;
    copyToClipboard(fullText, 'full');
  };

  const connectToMarketplace = (marketId: string) => {
    if (marketId === 'ebay') {
      authService.initiateOAuth(marketId);
    } else {
      setStatusToast(`Marketplace "${marketId}" configuration is in offline simulation mode.`);
      setTimeout(() => setStatusToast(null), 4000);
    }
  };

  const handleSimulatedPost = (marketId: string) => {
    setIsPosting(prev => ({ ...prev, [marketId]: true }));
    setTimeout(() => {
      setIsPosting(prev => ({ ...prev, [marketId]: false }));
      setStatusToast(`Successfully posted to ${marketId} via API! (Simulation Mode)`);
      setTimeout(() => setStatusToast(null), 4000);
    }, 1500);
  };

  const handleGoogleSignIn = () => {
    setGoogleUser({ name: "SlabLogic User", email: "user@gmail.com", picture: "" });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {statusToast && (
          <div className="bg-indigo-600 text-white text-xs font-bold px-4 py-2.5 flex items-center justify-between">
            <span>{statusToast}</span>
            <button onClick={() => setStatusToast(null)} className="text-indigo-200 hover:text-white font-bold ml-2">✕</button>
          </div>
        )}
        <div className="flex justify-between items-center p-6 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-bold text-slate-900">Slab Listing Assistant</h2>
            {googleUser && (
              <div className="flex items-center gap-2 px-3 py-1 bg-green-50 text-green-700 rounded-full text-xs font-medium border border-green-100">
                <div className="w-2 h-2 rounded-full bg-green-500"></div>
                {googleUser.name}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            {!googleUser && (
              <button
                onClick={handleGoogleSignIn}
                className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
              >
                Sign in
              </button>
            )}
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="overflow-y-auto p-6 space-y-6 flex-1">

          {/* Quick Copy for Mobile */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 rounded-xl p-4 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex gap-3">
              <div className="bg-blue-100 p-2 rounded-lg text-blue-600 h-fit">
                <Share2 className="w-5 h-5" />
              </div>
              <div>
                <h4 className="font-semibold text-blue-900 text-sm">Quick Share</h4>
                <p className="text-blue-700/80 text-xs mt-1">Copy all details for Facebook/Instagram/Discord.</p>
              </div>
            </div>
            <button
              onClick={copyFullListing}
              className="whitespace-nowrap flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium text-sm transition-colors w-full sm:w-auto justify-center shadow-sm"
            >
              {copiedField === 'full' ? <Check className="w-4 h-4" /> : <ClipboardList className="w-4 h-4" />}
              {copiedField === 'full' ? 'Copied!' : 'Copy All'}
            </button>
          </div>

          {/* Marketplace Selector */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-slate-700">Marketplace Integrations</label>
              <span className="text-xs text-slate-400">Secure OAuth 2.0 Connected</span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {marketplaces.map(market => {
                const isSelected = selectedMarkets.includes(market.id);
                const isConnected = connectedAccounts[market.id];
                const Icon = market.icon;

                return (
                  <div
                    key={market.id}
                    className={`
                        relative flex flex-col p-3 rounded-xl border transition-all
                        ${isSelected ? 'border-blue-200 bg-blue-50/50' : 'border-slate-200 bg-white'}
                     `}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div
                        className="flex items-center gap-2 cursor-pointer"
                        onClick={() => toggleMarket(market.id)}
                      >
                        <div className={`p-1.5 rounded-lg ${market.bgColor} ${market.color}`}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <span className="font-medium text-slate-700 text-sm">{market.name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {market.apiCapable && (
                          isConnected ? (
                            <span className="flex items-center gap-1 text-[10px] font-medium text-green-600 bg-green-50 px-2 py-0.5 rounded-full border border-green-100">
                              <Check className="w-3 h-3" /> Linked
                            </span>
                          ) : (
                            <button
                              onClick={() => connectToMarketplace(market.id)}
                              className="text-[10px] font-medium text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 px-2 py-0.5 rounded-full transition-colors border border-blue-100"
                            >
                              Connect
                            </button>
                          )
                        )}
                        <button
                          onClick={() => toggleMarket(market.id)}
                          role="switch"
                          className={`w-5 h-5 rounded-full border flex items-center justify-center transition-colors ${isSelected ? 'bg-blue-600 border-blue-600' : 'bg-white border-slate-300'}`}
                        >
                          {isSelected && <Check className="w-3 h-3 text-white" />}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Marketplace Actions (Search & List) */}
          {selectedMarkets.length > 0 && (
            <div className="space-y-4">
              <h4 className="text-sm font-semibold text-slate-800">Actions</h4>
              <div className="space-y-3">
                {marketplaces.filter(m => selectedMarkets.includes(m.id)).map(market => {
                  const isConnected = connectedAccounts[market.id];
                  const posting = isPosting[market.id];

                  return (
                    <div key={market.id} className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-white rounded-xl border border-slate-200 gap-4 shadow-sm hover:border-slate-300 transition-colors">
                      <div className="flex items-center gap-3">
                        <market.icon className={`w-5 h-5 ${market.color}`} />
                        <div>
                          <div className="font-semibold text-slate-900 text-sm flex items-center gap-2">
                            {market.name}
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2 w-full sm:w-auto">
                        <a
                          href={market.searchUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-3 py-2 bg-white border border-slate-200 rounded-lg text-xs font-medium text-slate-600 hover:text-blue-600 hover:border-blue-300 transition-all shadow-sm"
                        >
                          <Search className="w-3.5 h-3.5" /> Check Comps
                        </a>

                        {isConnected ? (
                          <button
                            onClick={() => handleSimulatedPost(market.id)}
                            disabled={posting}
                            className="flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-3 py-2 bg-slate-900 border border-slate-900 rounded-lg text-xs font-medium text-white hover:bg-slate-800 transition-all shadow-sm min-w-[100px]"
                          >
                            {posting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Link2 className="w-3.5 h-3.5" />}
                            {posting ? 'Posting...' : 'Post API'}
                          </button>
                        ) : (
                          <a
                            href={market.createUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-3 py-2 bg-blue-600 border border-blue-600 rounded-lg text-xs font-medium text-white hover:bg-blue-700 transition-all shadow-sm"
                          >
                            <ExternalLink className="w-3.5 h-3.5" />
                            Create Listing
                          </a>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Listing Details Grid */}
          <div className="grid grid-cols-1 gap-6">
            {/* Title Section */}
            <div>
              <div className="flex justify-between items-end mb-2">
                <label className="text-sm font-medium text-slate-700">Listing Title</label>
                <button
                  onClick={() => copyToClipboard(card.listingDraft.title, 'title')}
                  className="text-xs flex items-center gap-1 text-blue-600 hover:text-blue-700 font-medium"
                >
                  {copiedField === 'title' ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
                </button>
              </div>
              <div className="bg-slate-50 p-3 rounded-lg border border-slate-200 text-slate-800 font-medium text-sm">
                {card.listingDraft.title}
              </div>
            </div>

            {/* Price Section */}
            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">Price Range</label>
              <div className="flex gap-4">
                <div className="bg-slate-50 p-3 rounded-lg border border-slate-200 text-slate-800 w-1/2 cursor-pointer" onClick={() => copyToClipboard(card.estimatedValue.min.toString(), 'min_price')}>
                  <span className="text-[10px] uppercase tracking-wider text-slate-500 block font-semibold mb-1">Low</span>
                  <div className="flex items-center justify-between">
                    <span className="text-lg font-bold text-slate-700">${card.estimatedValue.min}</span>
                    {copiedField === 'min_price' && <Check className="w-4 h-4 text-green-500" />}
                  </div>
                </div>
                <div className="bg-slate-50 p-3 rounded-lg border border-slate-200 text-slate-800 w-1/2 cursor-pointer" onClick={() => copyToClipboard(card.estimatedValue.max.toString(), 'max_price')}>
                  <span className="text-[10px] uppercase tracking-wider text-slate-500 block font-semibold mb-1">High</span>
                  <div className="flex items-center justify-between">
                    <span className="text-lg font-bold text-slate-700">${card.estimatedValue.max}</span>
                    {copiedField === 'max_price' && <Check className="w-4 h-4 text-green-500" />}
                  </div>
                </div>
              </div>
            </div>

            {/* Description Section */}
            <div>
              <div className="flex justify-between items-end mb-2">
                <label className="text-sm font-medium text-slate-700">Description</label>
                <button
                  onClick={() => copyToClipboard(card.listingDraft.description, 'desc')}
                  className="text-xs flex items-center gap-1 text-blue-600 hover:text-blue-700 font-medium"
                >
                  {copiedField === 'desc' ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
                </button>
              </div>
              <div className="bg-slate-50 p-3 rounded-lg border border-slate-200 text-slate-700 text-sm whitespace-pre-wrap h-32 overflow-y-auto">
                {card.listingDraft.description}
              </div>
            </div>

            {/* Tags Section */}
            <div>
              <div className="flex justify-between items-end mb-2">
                <label className="text-sm font-medium text-slate-700">Tags</label>
                <button
                  onClick={() => copyToClipboard(card.listingDraft.tags.join(', '), 'tags')}
                  className="text-xs flex items-center gap-1 text-blue-600 hover:text-blue-700 font-medium"
                >
                  {copiedField === 'tags' ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {card.listingDraft.tags.map(tag => (
                  <span key={tag} className="bg-slate-100 text-slate-600 px-2 py-1 rounded text-xs border border-slate-200 font-medium">
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          </div>

        </div>

        <div className="p-6 border-t border-slate-100 bg-slate-50 flex justify-end">
          <button onClick={onClose} className="bg-slate-900 text-white px-8 py-3 rounded-xl font-semibold hover:bg-slate-800 transition-colors shadow-sm">
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default ListingModal;